import React from 'react';
import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  Menu,
  Modal,
  Row,
  Switch,
  Tag,
} from 'antd';
import {
  CheckCircleOutlined,
  FilterOutlined,
  EllipsisOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { CopySpan, DataGrid, Label, Loading, ScreenMask, SiteContent, message } from 'components/common';
import { HTTP_STATUS_OK, LOADING_ICON_SIZE1 } from 'constants/config';
import {
  fetchProductSerialNumberList,
  updateProductSerialNumber,
  updateProductSerialNumberAvailableStatus,
} from 'services/product';

type Props = {
  onClose: Function;
  product: StringKAnyVPair;
  updateProduct: Function;
  visible: boolean;
};

const SerialNumberDialog = (props: Props) => {
  const DEFAULT_GRID_PAGE_SIZE = 20;
  const SHOW_AVAILABLE = 1;
  const SHOW_NOT_AVAILABLE = 2;
  const DEFAULT_DISPLAY_AVAILABLE = SHOW_AVAILABLE | SHOW_NOT_AVAILABLE;
  const STATUS_AVAILABLE = 1;
  const STATUS_NOT_AVAILABLE = 0;
  const { useRef, useState } = React;
  const [displayAvailable, setDisplayAvailable] = useState(DEFAULT_DISPLAY_AVAILABLE);
  const [enableSerialNumber, setEnableSerialNumber] = useState(false);
  const [filterKey, setFilterKey] = useState('');
  const [gridCount, setGridCount] = useState(0);
  const [inited, setInited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snList, setSnList] = useState<StringKAnyVPair[]>([]);
  const filterTimer = useRef<any>(null);
  const gridPageIndex = useRef<number>(0);
  const gridPageSize = useRef<number>(DEFAULT_GRID_PAGE_SIZE);

  const columns = [
    /*{
      header: 'SKU',
      name: 'sku',
      render(value: any) {
        const { data } = value;

        return (
          <CopySpan
            inGrid
            value={data.sku || ''}
          />
        );
      },
    },*/
    {
      header: 'Serial Number',
      name: 'serialNumber',
      render(value: any) {
        const { data } = value;

        return (
          <CopySpan
            inGrid
            value={data.serialNumber || ''}
          />
        );
      },
    },
    {
      header: 'Available',
      //name: '',
      maxWidth: 170,
      showColumnMenuTool: false,
      sortable: false,
      textAlign: 'center',
      render(row: any) {
        const { data } = row;

        return (
          <div className="ant-input-group-wrapper w-none">
            <Tag
              className="fs-14 mr-0 p-1-7 text-center"
              color={data.status === STATUS_AVAILABLE ? 'blue' : 'magenta'}
              //size="small"
              style={{ width: 102 }}
            >
              {data.status === STATUS_AVAILABLE ? 'Available' : 'Not Available'}
            </Tag>
            <Dropdown
              overlay={(
                <Menu>
                  <Menu.Item disabled={data.status === STATUS_AVAILABLE} key="available-btn" onClick={() => changeAvailable(data, true)}>
                    <Label className={data.status !== STATUS_AVAILABLE ? 'label-primary' : undefined}>
                      <CheckCircleOutlined />
                      &nbsp;
                      Available
                    </Label>
                  </Menu.Item>
                  <Menu.Item disabled={data.status !== STATUS_AVAILABLE} key="unavailable-btn" onClick={() => changeAvailable(data, false)}>
                    <Label className={data.status === STATUS_AVAILABLE ? 'label-danger' : undefined}>
                      <MinusCircleOutlined />
                      &nbsp;
                      Not Available
                    </Label>
                  </Menu.Item>
                </Menu>
              )}
            >
              <Button size="small">
                <EllipsisOutlined />
              </Button>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  const changeAvailable = (sd: StringKAnyVPair, available: boolean) => {
    const msg = !available ? 
      `Are you sure to set ${sd.serialNumber} not available?` :
      `Are you sure to set ${sd.serialNumber} available?`;

    Modal.confirm({
      title: 'Confirm',
      content: msg,
      onOk() {
        updateAvailable(sd, available);
      },
    });
  };

  const clearFiterTimer = () => {
    if (filterTimer.current) {
      clearTimeout(filterTimer.current);
      filterTimer.current = null;
    }
  };

  const getGridData = (gd: StringKAnyVPair[]) => {
    let dl = gd.filter(e => e.serialNumber.toLowerCase().indexOf(filterKey.toLowerCase()) > -1);

    dl = dl.filter(e => {
      const ma = (displayAvailable & SHOW_AVAILABLE) > 0 && (e.status === STATUS_AVAILABLE);
      const mna = (displayAvailable & SHOW_NOT_AVAILABLE) > 0 && (e.status === STATUS_NOT_AVAILABLE);

      return ma || mna;
    });

    return dl;
  };

  // eslint-disable-next-line
  const loadProductSerialNumberList = async () => {
    const params = {
      $count: 'true',
      $skip: gridPageIndex.current * gridPageSize.current,
      $sortBy: 'serialNumber desc',
      $top: gridPageSize.current,
      //$filter
    };

    //console.log(props.product);
    setIsLoading(true);
    try {
      const { count, data } = await fetchProductSerialNumberList(props.product.productId, params) as any;

      if (Array.isArray(data)) {
        setGridCount(count || 0);
        //setGridCount(count + 1000);
        setSnList(data);
      }
    } catch(e) {
      message.error(`Fetched serial number list error: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeEnableSerialNumber = async (chk: boolean) => {
    if (chk === enableSerialNumber) return;

    setIsLoading(true);
    setEnableSerialNumber(chk);
    try {
      const { code, isSuccess } = await updateProductSerialNumber(props.product.productId, chk) as any;

      if (code === HTTP_STATUS_OK) {
        if (isSuccess) {
          const ud = {
            //'enableSerialNumber': chk ? 1 : 0,
            'recheckSerialNumber': chk ? 1 : 0,
          };

          props.updateProduct(ud);
        } else {
          setEnableSerialNumber(!chk);
        }
      } else {
        setEnableSerialNumber(!chk);
      }
    } catch(e) {
      setEnableSerialNumber(!chk);
      message.error(`${chk ? 'Enable' : 'Disable'} the serial number error: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onClose = () => {
    props.onClose();
  };

  const onFilterChange = (evt: any) => {
    const str = evt.target.value.trim();
    //console.log(evt.target.value);
    filterTimer.current && clearFiterTimer();
    filterTimer.current = setTimeout(() => setFilterKey(str), 500);
  };

  const onLimitChange = (limit: number) => {
    //gridRef?.current.deselectAll();
    if (gridPageSize.current !== limit) {
      if (gridPageIndex.current * limit >= gridCount) gridPageIndex.current = 0;

      //setDisplayAvailable(DEFAULT_DISPLAY_AVAILABLE);
      gridPageSize.current = limit;
      loadProductSerialNumberList();
    }
  };

  const onSkipChange = (skip: number) => {
    const idx = skip / gridPageSize.current;
    //gridRef?.current.deselectAll();
    //console.log('s', skip, idx);
    if (gridPageIndex.current !== idx) {
      gridPageIndex.current = idx;
      //setDisplayAvailable(DEFAULT_DISPLAY_AVAILABLE);
      loadProductSerialNumberList();
    }
  };

  const onToggleDisplayAvailable = (showBit: number) => {
    const displayBit = (displayAvailable & showBit) > 0 ?
      displayAvailable ^ showBit : displayAvailable | showBit;

    //console.log('db', displayBit);
    setDisplayAvailable(displayBit);
  };

  const updateAvailable = async (sd: StringKAnyVPair, available: boolean) => {
    setIsLoading(true);
    try {
      const res = await updateProductSerialNumberAvailableStatus(
        props.product.productId,
        available,
        [sd.serialNumber],
      ) as any;
      //console.log('r', res);
      if (res && typeof res === 'object' && res.code === HTTP_STATUS_OK) {
        res.isSuccess && loadProductSerialNumberList();
      }
    } catch(e) {
      message.error(`Set serial number available status error: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (!inited) {
      loadProductSerialNumberList();
      //setEnableSerialNumber(props.product.enableSerialNumber === 1);
      setEnableSerialNumber(props.product.recheckSerialNumber === 1);
      setInited(true);
    }
  }, [inited, loadProductSerialNumberList, props]);

  return (<>
    <Modal
      centered
      destroyOnClose={true}
      footer={null}
      maskClosable={false}
      onCancel={onClose}
      title="Serial Number"
      visible={props.visible}
      width={900}
    >
      <SiteContent className="common-content">
        <div>
          <Label className="tail-colon2">Use Serial Number</Label>
          <Switch
            checked={props.product.enableSerialNumber}
            checkedChildren="Yes"
            disabled
            unCheckedChildren="No"
            //onChange={onChangeEnableSerialNumber}
          />
        </div>
        <div style={{marginTop: 12}}>
          <Label className="tail-colon2">Recheck Serial Number</Label>
          <Switch
            checked={enableSerialNumber}
            checkedChildren="Yes"
            unCheckedChildren="No"
            onChange={onChangeEnableSerialNumber}
          />
        </div>
        <Row align="middle" className="mt-12">
          <Input
            allowClear
            onChange={onFilterChange}
            placeholder="Filter"
            prefix={<FilterOutlined />}
            style={{ width: 150 }}
          />
          <Checkbox
            className="ml-12"
            checked={(displayAvailable & SHOW_AVAILABLE) > 0}
            onChange={() => onToggleDisplayAvailable(SHOW_AVAILABLE)}
          >
            Show Available
          </Checkbox>
          <Checkbox
            checked={(displayAvailable & SHOW_NOT_AVAILABLE) > 0}
            onChange={() => onToggleDisplayAvailable(SHOW_NOT_AVAILABLE)}
          >
            Show Not Available
          </Checkbox>
        </Row>
        <div style={{marginTop: 12}}>
          <DataGrid
            columns={columns}
            count={gridCount}
            dataSource={getGridData(snList)}
            limit={gridPageSize.current}
            onLimitChange={onLimitChange}
            onSkipChange={onSkipChange}
            pageSizes={[20, 50, 100, 200]}
            pagination
            rowKey="productSerialNumberNum"
            style={{height: 'calc(100vh - 300px)', maxHeight: 700}}
          />
        </div>
      </SiteContent>
    </Modal>
    {isLoading && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default SerialNumberDialog;
